import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/shared/services/query-keys';
import { useAuthStore } from '@/shared/stores/auth-store';
import type { Campaign } from '@/shared/types/entities';
import { CampaignStatus } from '@/shared/types/enums';
import {
  fetchCampaigns,
  fetchCampaign,
  fetchCampaignMetrics,
  fetchEvents,
  fetchAccountsByPlatform,
  fetchMetaPages,
  fetchMetaPagePosts,
  searchLocations,
  searchInterests,
  searchBehaviors,
  fetchAssetAccessUrl,
} from './campaigns-api';

export function useCampaigns(filters?: Record<string, unknown>) {
  const orgId = useAuthStore((state) => state.orgId);
  const queryFilters = {
    organizationId: orgId,
    page: filters?.page,
    limit: filters?.pageSize,
    status: filters?.status,
    platform: filters?.platform,
  };

  return useQuery({
    queryKey: queryKeys.campaigns.list(queryFilters),
    queryFn: () => fetchCampaigns(queryFilters),
    enabled: !!orgId,
  });
}

/**
 * Internal Promote statuses that may transition to something else via the
 * ad-platform status sync and therefore warrant polling while the page is
 * open. Terminal states (COMPLETED, ARCHIVED) are excluded.
 */
const POLLABLE_STATUSES = new Set<CampaignStatus>([
  CampaignStatus.PENDING_REVIEW,
  CampaignStatus.ACTIVE,
  CampaignStatus.PAUSED,
  CampaignStatus.REJECTED,
]);

/** Poll cadence for detail pages — tighter than the server cron, but still
 *  gentle enough to not hammer the API when a user is staring at the page. */
const STATUS_POLL_INTERVAL_MS = 30_000;

interface UseCampaignOptions {
  /**
   * When true, the query refetches every {@link STATUS_POLL_INTERVAL_MS} if
   * the campaign's status can still change based on platform-side decisions.
   * Use this on pages where the organizer is likely watching the outcome of
   * a recent launch.
   */
  pollPlatformStatus?: boolean;
}

export function useCampaign(id: string, options: UseCampaignOptions = {}) {
  return useQuery({
    queryKey: queryKeys.campaigns.detail(id),
    queryFn: () => fetchCampaign(id),
    enabled: !!id,
    refetchInterval: (query) => {
      if (!options.pollPlatformStatus) return false;
      const campaign = query.state.data as Campaign | undefined;
      if (!campaign) return STATUS_POLL_INTERVAL_MS;
      return POLLABLE_STATUSES.has(campaign.status) ? STATUS_POLL_INTERVAL_MS : false;
    },
  });
}

export function useCampaignMetrics(id: string) {
  return useQuery({
    queryKey: queryKeys.campaigns.metrics(id),
    queryFn: () => fetchCampaignMetrics(id),
    enabled: !!id,
  });
}

// ---- Wizard queries ----

export function useEvents() {
  const orgId = useAuthStore((state) => state.orgId);
  return useQuery({
    queryKey: queryKeys.events.list({ organizationId: orgId ?? '' }),
    queryFn: () => fetchEvents(orgId!),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAccountsByPlatform(platform: string) {
  const orgId = useAuthStore((state) => state.orgId);
  return useQuery({
    queryKey: queryKeys.accounts.byPlatform(`${orgId ?? 'none'}:${platform}`),
    queryFn: () => fetchAccountsByPlatform(orgId!, platform),
    enabled: !!platform && !!orgId,
  });
}

/**
 * Lists Facebook Pages the user can publish ads as on a given Meta ad
 * account. Drives the Page picker in the wizard. Cached for 5 minutes —
 * Pages don't change often and Meta /me/accounts is rate-limited.
 */
export function useMetaPages(adAccountId?: string) {
  return useQuery({
    queryKey: queryKeys.accounts.metaPages(adAccountId ?? 'none'),
    queryFn: () => fetchMetaPages(adAccountId!),
    enabled: !!adAccountId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Published posts on a Page that the organizer can boost. Kept on the same
 * 5-minute stale window as the Pages list — new posts are rare mid-wizard, and
 * the underlying Meta endpoint is rate-limited.
 */
export function useMetaPagePosts(adAccountId?: string, pageId?: string) {
  return useQuery({
    queryKey: queryKeys.accounts.metaPagePosts(adAccountId ?? 'none', pageId ?? 'none'),
    queryFn: () => fetchMetaPagePosts(adAccountId!, pageId!),
    enabled: !!adAccountId && !!pageId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLocationSearch(query: string) {
  return useQuery({
    queryKey: queryKeys.targeting.locations(query),
    queryFn: () => searchLocations(query),
    enabled: query.length >= 2,
    staleTime: 30 * 1000,
  });
}

export function useInterestSearch(query: string) {
  return useQuery({
    queryKey: queryKeys.targeting.interests(query),
    queryFn: () => searchInterests(query),
    enabled: query.length >= 2,
    staleTime: 30 * 1000,
  });
}

export function useBehaviorSearch(query: string) {
  return useQuery({
    queryKey: queryKeys.targeting.behaviors(query),
    queryFn: () => searchBehaviors(query),
    enabled: query.length >= 2,
    staleTime: 30 * 1000,
  });
}

/**
 * Resolves a private storage key (e.g. a carousel card image) to a
 * short-lived signed URL. staleTime stays under the signed-URL expiry so a
 * remount never renders a link that already expired.
 */
export function useAssetAccessUrl(storageKey?: string) {
  return useQuery({
    queryKey: queryKeys.ai.assetAccess(storageKey ?? 'none'),
    queryFn: () => fetchAssetAccessUrl(storageKey!),
    enabled: !!storageKey,
    staleTime: 4 * 60 * 1000,
  });
}
